/* global Word, OfficeExtension */

import { shouldGenerateWordComment } from "@/utility/shouldGenerateWordComment";
import { wordTask } from "./wordTask";
import { applyDraftStyle, loadFontStyle } from "@/lib/styleService";
import { isTemplateHeadingParagraph } from "@/utility/wordStyleAdoption";
import Logger from "@/utility/logger";

const applyHeaderFormat = async (paragraph: Word.Paragraph, level: number) => {
  const draftStyles = await loadFontStyle("draftStyles").catch(() => null);
  const draftColor = draftStyles?.fontColor || "#1F6B3E";
  const draftFont = draftStyles?.fontFamily || "Courier";
  const draftSize = draftStyles?.fontSize || 10;

  if (level === 4) {
    paragraph.styleBuiltIn = Word.BuiltInStyleName.normal;
    paragraph.font.name = draftFont;
    paragraph.font.size = draftSize;
    paragraph.font.color = draftColor;
    await applyDraftStyle(paragraph.getRange());
    paragraph.font.underline = "Single";
    paragraph.font.bold = true;
  } else {
    if (level === 1) paragraph.styleBuiltIn = Word.BuiltInStyleName.heading1;
    else if (level === 2) paragraph.styleBuiltIn = Word.BuiltInStyleName.heading2;
    else if (level === 3) paragraph.styleBuiltIn = Word.BuiltInStyleName.heading3;
    paragraph.font.name = draftFont;
    paragraph.font.size = draftSize;
    paragraph.font.color = draftColor;
    paragraph.font.bold = true;
  }

  try {
    const runs = (paragraph as any).runs?.load?.("items");
    await paragraph.context.sync();
    if (runs?.items) {
      for (const r of runs.items) {
        r.font.name = draftFont;
        r.font.size = draftSize;
        r.font.color = draftColor;
        r.font.bold = true;
      }
    }
  } catch (_) {
    // best-effort
  }
};

const applyBodyParagraphFormat = async (paragraph: Word.Paragraph) => {
  paragraph.styleBuiltIn = Word.BuiltInStyleName.normal;
  try {
    const draftStyles = await loadFontStyle("draftStyles");
    if (draftStyles?.fontFamily) paragraph.font.name = draftStyles.fontFamily;
    if (draftStyles?.fontSize) paragraph.font.size = draftStyles.fontSize;
    if (draftStyles?.fontColor) paragraph.font.color = draftStyles.fontColor;
  } catch (_) {
    paragraph.font.name = "Courier";
    paragraph.font.size = 10;
    paragraph.font.color = "#1F6B3E";
  }
  await applyDraftStyle(paragraph.getRange());
  paragraph.alignment = Word.Alignment.justified;
};

const applyListParagraphFormat = async (paragraph: Word.Paragraph) => {
  await applyDraftStyle(paragraph.getRange());
  paragraph.alignment = Word.Alignment.justified;
};

type OrderedListKind = "arabic" | "lowerLetter" | "lowerRoman";

const MAX_LIST_LEVEL = 8;

type OrderedListMarkerFormat = "dot" | "trailingParen" | "surroundingParen";

type OrderedListMarkerCandidate = {
  kind: OrderedListKind;
  value: number;
};

type OrderedListMarker = {
  indentation: string;
  marker: string;
  markerFormat: OrderedListMarkerFormat;
  token: string;
  candidates: OrderedListMarkerCandidate[];
};

type OrderedListSequenceState = {
  kind: OrderedListKind;
  nextValue: number;
};

type OrderedListInferenceState = {
  stack: Array<OrderedListSequenceState | undefined>;
  levelNumberingKinds: Partial<Record<number, OrderedListKind>>;
  lastLevel: number;
};

const romanNumeralValues: Record<string, number> = {
  i: 1,
  v: 5,
  x: 10,
  l: 50,
  c: 100,
  d: 500,
  m: 1000,
};

const numberToRoman = (value: number): string => {
  const romanParts: Array<[number, string]> = [
    [1000, "m"],
    [900, "cm"],
    [500, "d"],
    [400, "cd"],
    [100, "c"],
    [90, "xc"],
    [50, "l"],
    [40, "xl"],
    [10, "x"],
    [9, "ix"],
    [5, "v"],
    [4, "iv"],
    [1, "i"],
  ];

  let remaining = value;
  let result = "";

  for (const [amount, numeral] of romanParts) {
    while (remaining >= amount) {
      result += numeral;
      remaining -= amount;
    }
  }

  return result;
};

const romanToNumber = (value: string): number | null => {
  const normalizedValue = value.toLowerCase();
  if (!/^[ivxlcdm]+$/.test(normalizedValue)) {
    return null;
  }

  let total = 0;
  for (let i = 0; i < normalizedValue.length; i++) {
    const current = romanNumeralValues[normalizedValue[i]];
    const next = romanNumeralValues[normalizedValue[i + 1]];

    total += next && current < next ? -current : current;
  }

  if (total <= 0 || total > 3999 || numberToRoman(total) !== normalizedValue) {
    return null;
  }

  return total;
};

const letterToNumber = (value: string): number => {
  return value
    .toLowerCase()
    .split("")
    .reduce((total, char) => total * 26 + char.charCodeAt(0) - 96, 0);
};

const getMarkdownListLevel = (indentation: string) => {
  const expandedIndentation = indentation.replace(/\t/g, "    ").length;
  if (expandedIndentation === 0) {
    return 0;
  }

  // LLM output commonly uses either 2 or 4 spaces for a single nested level.
  return Math.min(MAX_LIST_LEVEL, Math.max(1, Math.ceil(expandedIndentation / 4)));
};

// Matches one or more consecutive line-break characters, covering all common variants:
//   \r       — Carriage Return (CR, U+000D), used in old Mac line endings
//   \n       — Line Feed (LF, U+000A), standard Unix/Linux line ending
//   \v, \u000b — Vertical Tab (U+000B), an obscure ASCII control character
//   \u2028   — Unicode Line Separator
//   \u2029   — Unicode Paragraph Separator
// The `+` quantifier collapses runs of mixed line-break characters into a single match,
// e.g. "\r\n\r\n" (Windows CRLF pairs) or "\n\n\n" are each treated as one split point.
const markdownLineBreakRegex = /[\r\n\v\u000b\u2028\u2029]+/;
// Matches the leading marker of a Markdown list line (unordered or ordered), with optional indentation:
//   ^        — anchors to the start of the string
//   \s*      — zero or more leading whitespace chars (spaces/tabs representing indentation level)
//   (?:...) — non-capturing group for the list marker itself, which is either:
//     [-*+~]   — a single unordered bullet character (hyphen, asterisk, plus, or tilde)
//     |        — OR
//     \d+\.    — one or more digits followed by a literal dot (e.g. "1.", "12." for ordered lists)
//   \s+      — one or more spaces/tabs that must follow the marker (the required gap before content)
// Used to detect whether a line is a list item and to strip the marker prefix from it.
const orderedListLineRegex = /^(\s*)(?:(\(([0-9]+|[a-zA-Z]+)\))|([0-9]+|[a-zA-Z]+)([.)]))\s+/;
const markdownListLineRegex = /^\s*(?:[-*+~]|\([0-9a-zA-Z]+\)|[0-9a-zA-Z]+[.)])\s+/;

const parseOrderedListMarker = (text: string): OrderedListMarker | null => {
  const match = text.match(orderedListLineRegex);
  if (!match) {
    return null;
  }

  const token = match[3] ?? match[4];
  const markerFormat: OrderedListMarkerFormat = match[3] ? "surroundingParen" : match[5] === ")" ? "trailingParen" : "dot";
  const candidates: OrderedListMarkerCandidate[] = [];

  if (/^\d+$/.test(token)) {
    candidates.push({ kind: "arabic", value: parseInt(token, 10) });
  } else {
    const lowerToken = token.toLowerCase();
    const romanValue = romanToNumber(lowerToken);

    candidates.push({ kind: "lowerLetter", value: letterToNumber(lowerToken) });

    // Single-letter i/v/x markers are ambiguous, so keep both candidates.
    // Multi-letter canonical Roman numerals are almost always intended as Roman list markers.
    if (romanValue !== null && (lowerToken.length > 1 || /^[ivx]$/.test(lowerToken))) {
      if (lowerToken.length > 1) {
        candidates.unshift({ kind: "lowerRoman", value: romanValue });
      } else {
        candidates.push({ kind: "lowerRoman", value: romanValue });
      }
    }
  }

  return {
    indentation: match[1],
    marker: match[0],
    markerFormat,
    token,
    candidates,
  };
};

const getCandidateForKind = (
  candidates: OrderedListMarkerCandidate[],
  kind: OrderedListKind | undefined
): OrderedListMarkerCandidate | undefined => {
  return kind ? candidates.find((candidate) => candidate.kind === kind) : undefined;
};

const getRestartCandidate = (candidates: OrderedListMarkerCandidate[]): OrderedListMarkerCandidate => {
  return candidates.find((candidate) => candidate.value === 1) ?? candidates[0];
};

const chooseOrderedListCandidate = (
  marker: OrderedListMarker,
  currentLevelState: OrderedListSequenceState | undefined,
  levelNumberingKind: OrderedListKind | undefined
): OrderedListMarkerCandidate => {
  return (
    getCandidateForKind(marker.candidates, levelNumberingKind) ??
    getCandidateForKind(marker.candidates, currentLevelState?.kind) ??
    getRestartCandidate(marker.candidates)
  );
};

const markersShareValue = (
  currentCandidate: OrderedListMarkerCandidate,
  nextMarker: OrderedListMarker | null
): boolean => {
  if (!nextMarker) {
    return false;
  }

  return nextMarker.candidates.some(
    (nextCandidate) => nextCandidate.kind === currentCandidate.kind && nextCandidate.value === currentCandidate.value
  );
};

const inferOrderedListLevel = (
  marker: OrderedListMarker,
  state: OrderedListInferenceState,
  isTableCellParagraph: boolean,
  nextMarker: OrderedListMarker | null
): { level: number; candidate: OrderedListMarkerCandidate } => {
  const explicitLevel = isTableCellParagraph || marker.indentation.length === 0 ? null : getMarkdownListLevel(marker.indentation);

  if (explicitLevel !== null) {
    const level = Math.min(MAX_LIST_LEVEL, explicitLevel);
    return {
      level,
      candidate: chooseOrderedListCandidate(marker, state.stack[level], state.levelNumberingKinds[level]),
    };
  }

  const continuationMatches: Array<{ level: number; candidate: OrderedListMarkerCandidate }> = [];

  for (let level = 0; level < state.stack.length; level++) {
    const levelState = state.stack[level];
    if (!levelState) {
      continue;
    }

    const candidate = marker.candidates.find(
      (possibleCandidate) =>
        possibleCandidate.kind === levelState.kind && possibleCandidate.value === levelState.nextValue
    );

    if (candidate) {
      continuationMatches.push({ level, candidate });
    }
  }

  if (continuationMatches.length > 0) {
    const deepestMatch = continuationMatches[continuationMatches.length - 1];
    const shallowestMatch = continuationMatches[0];
    const currentLevelMatch = continuationMatches.find((match) => match.level === state.lastLevel);
    const parentLevelMatch = continuationMatches.find((match) => match.level === state.lastLevel - 1);

    if (markersShareValue(deepestMatch.candidate, nextMarker)) {
      return deepestMatch;
    }

    if (currentLevelMatch && !parentLevelMatch) {
      return currentLevelMatch;
    }

    return shallowestMatch;
  }

  const restartCandidate = getRestartCandidate(marker.candidates);
  const level = state.stack.length === 0 ? 0 : Math.min(MAX_LIST_LEVEL, state.lastLevel + 1);

  return { level, candidate: restartCandidate };
};

const updateOrderedListInferenceState = (
  state: OrderedListInferenceState,
  level: number,
  candidate: OrderedListMarkerCandidate
) => {
  state.stack = state.stack.slice(0, level + 1);
  state.stack[level] = {
    kind: candidate.kind,
    nextValue: candidate.value + 1,
  };
  state.levelNumberingKinds[level] = state.levelNumberingKinds[level] ?? candidate.kind;
  state.lastLevel = level;
};

const resetOrderedListInferenceState = (state: OrderedListInferenceState) => {
  state.stack = [];
  state.levelNumberingKinds = {};
  state.lastLevel = 0;
};

const getMarkdownListLines = (text: string): string[] | null => {
  if (!markdownLineBreakRegex.test(text)) {
    return null;
  }

  const lines = text
    .split(markdownLineBreakRegex)
    .map((line) => line.replace(/\s+$/, ""))
    .filter((line) => line.trim().length > 0);

  if (lines.length < 2 || !lines.every((line) => markdownListLineRegex.test(line))) {
    return null;
  }

  return lines;
};

const getTableCellListLines = (text: string): string[] | null => {
  if (!markdownLineBreakRegex.test(text)) {
    return null;
  }

  const lines = text
    .split(markdownLineBreakRegex)
    .map((line) => line.replace(/\s+$/, ""))
    .filter((line) => line.trim().length > 0);

  if (lines.length < 2 || !lines.some((line) => markdownListLineRegex.test(line))) {
    return null;
  }

  return lines;
};

const markdownLinkRegex = /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g;

const getMarkdownLinkMatches = (text: string) => {
  const matches: Array<{ fullMatch: string; label: string; url: string }> = [];
  const regex = new RegExp(markdownLinkRegex);

  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    matches.push({
      fullMatch: match[0],
      label: match[1],
      url: match[2],
    });
  }

  return matches;
};

// Formats the document by handling markdown-like syntax and cleaning up empty lines.
export const formatSection = async (targetWord: string, existingContext?: Word.RequestContext) => {
  try {
    const executeFormatting = async (context: Word.RequestContext) => {
    const startBookmark = context.document.getBookmarkRangeOrNullObject(targetWord + "_START");
      const endBookmark = context.document.getBookmarkRangeOrNullObject(targetWord + "_END");

      startBookmark.load("isNullObject");
      endBookmark.load("isNullObject");
      await context.sync();

      if (startBookmark.isNullObject || endBookmark.isNullObject) {
        Logger.warn("wordFormatting.ts: One or both bookmarks were not found.", targetWord);

        // DEBUGGING PURPOSE
        if (startBookmark.isNullObject)
          Logger.warn(`wordFormatting.ts: Start bookmark "${targetWord}_START" was not found.`);

        // DEBUGGING PURPOSE
        if (endBookmark.isNullObject) Logger.warn(`wordFormatting.ts: End bookmark "${targetWord}_END" was not found.`);

        return;
      } // Get the range between the two bookmarks

      const processRange = startBookmark.expandTo(endBookmark);
      // --- 1. Handle Paragraph-level Formatting (Headers and Lists) ---
      const paragraphs = processRange.paragraphs;
      paragraphs.load("items/text, items/style, items/styleBuiltIn"); // Load paragraph items, styles, and text
      await context.sync();

      const loadTableCellParagraphFlags = async (paragraphItems: Word.Paragraph[]) => {
        const parentCells = paragraphItems.map((para) => {
          const parentCell = para.parentTableCellOrNullObject;
          parentCell.load("isNullObject");
          return parentCell;
        });

        await context.sync();
        return parentCells.map((parentCell) => !parentCell.isNullObject);
      };

      let tableCellParagraphFlags = await loadTableCellParagraphFlags(paragraphs.items);
      let splitListParagraphs = false;
      for (let paraIndex = 0; paraIndex < paragraphs.items.length; paraIndex++) {
        const para = paragraphs.items[paraIndex];
        const listLines = tableCellParagraphFlags[paraIndex]
          ? getTableCellListLines(para.text)
          : getMarkdownListLines(para.text);

        if (!listLines) {
          continue;
        }

        para.insertText(listLines[0], Word.InsertLocation.replace);

        let previousParagraph = para;
        for (const listLine of listLines.slice(1)) {
          previousParagraph = previousParagraph.insertParagraph(listLine, Word.InsertLocation.after);
        }

        splitListParagraphs = true;
      }

      if (splitListParagraphs) {
        await context.sync();
        paragraphs.load("items/text");
        await context.sync();
      }

      tableCellParagraphFlags = await loadTableCellParagraphFlags(paragraphs.items);

      // ── Pass 1: Headers + Bullets ──────────────────────────────────────
      let activeBulletList: { id: number; kind: "bullet" } | null = null;

      // IMPORTANT: Loop from 0 to process all paragraphs in the range.
      for (let i = 0; i < paragraphs.items.length; i++) {
        const para = paragraphs.items[i];
        if (isTemplateHeadingParagraph(para, i, paragraphs.items.length)) {
          activeBulletList = null;
          continue;
        }
        const isTableCellParagraph = tableCellParagraphFlags[i];

        // ... inside the loop
        // 1. Get raw text (don't trim yet) to detect position and hidden breaks
        const rawText = para.text;

        const headerMatch = rawText.match(/(.*?)(#{1,4})\s+([\s\S]*)/);

        if (headerMatch) {
          // Group 1: (.*?) => Text BEFORE hashes
          const precedingText = headerMatch[1];
          // Group 2: (#{1,4}) => The hashes
          const hashes = headerMatch[2];
          // Group 3: ([\s\S]*) => The content (matches newlines/soft returns also)
          const rawContent = headerMatch[3]; // This might contain "Title\vBody"
          const level = hashes.length;

          let headerTitle = rawContent;
          let bodyText = "";

          // searching for any line break characters (\r, \n, \v = vertical tab/soft return)
          // word often uses \r or \v for soft returns within a paragraph.
          const splitIndex = rawContent.search(/[\r\n\v\u000b\u2028\u2029]/);

          // we found a break! let's split the header from the body.
          if (splitIndex !== -1) {
            headerTitle = rawContent.substring(0, splitIndex).trim();
            bodyText = rawContent.substring(splitIndex).trim();
          } else {
            headerTitle = rawContent.trim();
          }

          if (precedingText.trim().length > 0) {
            Logger.debug("wordFormatting.ts: Splitting run-on paragraph (Text -> Header)", targetWord);
            para.insertText(precedingText.trim(), Word.InsertLocation.replace);

            const headerPara = para.insertParagraph(headerTitle, Word.InsertLocation.after);

            await applyHeaderFormat(headerPara, level);

            if (bodyText) {
              const bodyParagraph = headerPara.insertParagraph(bodyText, Word.InsertLocation.after);
              await applyBodyParagraphFormat(bodyParagraph);
            }
          } else {
            para.insertText(headerTitle, Word.InsertLocation.replace);

            await applyHeaderFormat(para, level);

            if (bodyText) {
              Logger.debug("wordFormatting.ts: Splitting header paragraph (Header -> Body)", targetWord);
              const bodyParagraph = para.insertParagraph(bodyText, Word.InsertLocation.after);
              await applyBodyParagraphFormat(bodyParagraph);
            }
          }

          activeBulletList = null;
          continue;
        }

        const boldHeadingMatch = rawText.trim().match(/^\*\*([^*\n]+?)\*\*(.*)$/);
        if (boldHeadingMatch && (boldHeadingMatch[2].trim() === "" || /^(?:\s*(?:\[[^\]]*\]|<!--[\s\S]*?-->|\u2726|\u2727|\*|\.))+$/.test(boldHeadingMatch[2]))) {
          const headerTitle = boldHeadingMatch[1].trim() + (boldHeadingMatch[2] ? " " + boldHeadingMatch[2].trim() : "");
          para.insertText(headerTitle, Word.InsertLocation.replace);
          para.styleBuiltIn = Word.BuiltInStyleName.normal;
          const draftStyles = await loadFontStyle("draftStyles").catch(() => null);
          const draftColor = draftStyles?.fontColor || "#1F6B3E";
          const draftFont = draftStyles?.fontFamily || "Courier";
          const draftSize = draftStyles?.fontSize || 10;
          para.font.name = draftFont;
          para.font.size = draftSize + 1;
          para.font.color = draftColor;
          para.font.bold = true;
          activeBulletList = null;
          continue;
        }

        const bulletMatch = rawText.match(/^(\s*)([-*+~])\s+/);
        if (bulletMatch) {
          const level = isTableCellParagraph ? 0 : getMarkdownListLevel(bulletMatch[1]);
          const marker = bulletMatch[0];

          const searchResult = para.search(marker, { matchCase: true, matchWholeWord: false });
          const markerRange = searchResult.getFirst();
          markerRange.delete();

          let list: Word.List;
          if (activeBulletList?.kind === "bullet") {
            list = para.attachToList(activeBulletList.id, level);
          } else {
            list = para.startNewList();
            list.load("id");
            await context.sync();
            activeBulletList = { id: list.id, kind: "bullet" };
          }

          list.setLevelBullet(level, Word.ListBullet.solid);
          para.listItem.level = level;
          await applyListParagraphFormat(para);
          continue;
        }

        activeBulletList = null;
      }

      // One single sync call after the loop processes all queued changes.
      await context.sync();

      // ── Pass 2: Ordered Lists ─────────────────────────────────────────
      const numberingMap: Record<OrderedListKind, Word.ListNumbering> = {
        arabic: Word.ListNumbering.arabic,
        lowerLetter: Word.ListNumbering.lowerLetter,
        lowerRoman: Word.ListNumbering.lowerRoman,
      };

      let activeOrderedList: { id: number } | null = null;
      const orderedListState: OrderedListInferenceState = {
        stack: [],
        levelNumberingKinds: {},
        lastLevel: 0,
      };

      const getNextOrderedMarker = (startIndex: number): OrderedListMarker | null => {
        for (let nextIndex = startIndex; nextIndex < paragraphs.items.length; nextIndex++) {
          const nextText = paragraphs.items[nextIndex].text;
          const nextMarker = parseOrderedListMarker(nextText);

          if (nextMarker) {
            return nextMarker;
          }

          if (nextText.trim().length > 0) {
            return null;
          }
        }

        return null;
      };

      for (let i = 0; i < paragraphs.items.length; i++) {
        const para = paragraphs.items[i];
        const isTableCellParagraph = tableCellParagraphFlags[i];
        const rawText = para.text;
        const orderedMarker = parseOrderedListMarker(rawText);

        if (orderedMarker) {
          const nextMarker = getNextOrderedMarker(i + 1);
          const { level, candidate } = inferOrderedListLevel(
            orderedMarker,
            orderedListState,
            isTableCellParagraph,
            nextMarker
          );
          updateOrderedListInferenceState(orderedListState, level, candidate);
          const numberingKind = orderedListState.levelNumberingKinds[level] ?? candidate.kind;

          const searchResult = para.search(orderedMarker.marker, { matchCase: true, matchWholeWord: false });
          searchResult.getFirst().delete();

          let list: Word.List;
          if (activeOrderedList) {
            list = para.attachToList(activeOrderedList.id, level);
          } else {
            list = para.startNewList();
            list.load("id");
            await context.sync();
            activeOrderedList = { id: list.id };
          }

          list.setLevelNumbering(level, numberingMap[numberingKind], [level, "."]);
          para.listItem.level = level;
          await applyListParagraphFormat(para);
          continue;
        }

        activeOrderedList = null;

        // Non-empty non-list content resets the auto-nesting context
        if (rawText.trim().length > 0) {
          resetOrderedListInferenceState(orderedListState);
        }
      }

      await context.sync();

      // --- 2. Handle Inline Formatting (Bold and Italic) ---
      // This runs after paragraph/list parsing so markdown list markers such as `* `
      // are removed before we look for inline emphasis.
      const boldSearchResults = processRange.search("\\*\\*(*)\\*\\*", { matchWildcards: true });

      boldSearchResults.load("items, font, text");

      await context.sync();

      for (const foundRange of boldSearchResults.items) {
        const originalText = foundRange.text;
        const newText = originalText.substring(2, originalText.length - 2);

        foundRange.insertText(newText, Word.InsertLocation.replace);
        foundRange.font.bold = true;
      }

      await context.sync();

      const italicSearchResults = processRange.search("\\*(*)\\*", { matchWildcards: true });

      italicSearchResults.load("items, font, text");

      await context.sync();

      for (const foundRange of italicSearchResults.items) {
        const originalText = foundRange.text;
        const newText = originalText.substring(1, originalText.length - 1);

        foundRange.font.italic = true;
        foundRange.insertText(newText, Word.InsertLocation.replace);
      }

      await context.sync();

      processRange.load("paragraphs/items/text");
      await context.sync();

      for (const para of processRange.paragraphs.items) {
        const rawText = para.text;
        const matches = getMarkdownLinkMatches(rawText);
        if (matches.length === 0) {
          continue;
        }

        for (const match of matches) {
          const { fullMatch, label, url } = match;

          const markdownLinkResults = para.search(fullMatch, { matchCase: true, matchWholeWord: false });
          markdownLinkResults.load("items");
          await context.sync();

          if (markdownLinkResults.items.length === 0) {
            continue;
          }

          const insertedLinkRange = markdownLinkResults.items[0].insertText(label, Word.InsertLocation.replace);
          insertedLinkRange.hyperlink = url;
          insertedLinkRange.styleBuiltIn = Word.BuiltInStyleName.hyperlink;
          await context.sync();
        }
      }

      Logger.debug("wordFormatting.ts: Appendix logs: Searching for cross-references...", targetWord);

      await wordTask(context, "JENLY_NO_DATA", processRange, true, true);
      await wordTask(context, "[Missing data:", processRange, true, true);

      let shouldGenerateTodoComments = shouldGenerateWordComment("JENLY_GENERATE_COMMENTS");

      await wordTask(context, "TODO:", processRange, true, shouldGenerateTodoComments);

      await wordTask(context, "information not available in the provided context", processRange, true);

      const hyperlinkSearchResults = processRange.search("[Aa]ppendix [0-9.]{1,}", {
        matchWildcards: true,
        ignoreSpace: true,
        matchCase: false,
      });

      hyperlinkSearchResults.load(
        "items/text, font/name, font/size, font/bold, font/italic, font/color, font/highlightColor"
      );

      await context.sync();

      if (hyperlinkSearchResults.items.length > 0) {
        for (const foundRange of hyperlinkSearchResults.items) {
          const originalFont = {
            name: foundRange.font.name,
            size: foundRange.font.size,
            italic: foundRange.font.italic,
            color: foundRange.font.color,
            highlightColor: foundRange.font.highlightColor,
          };

          const foundText = foundRange.text.trim();
          let linkRange = foundRange;

          let normalizedText = foundText;
          if (normalizedText.endsWith(".")) {
            normalizedText = normalizedText.slice(0, -1);
            linkRange = foundRange.search(normalizedText, { matchCase: true }).getFirst();
            await context.sync();
          }

          const targetHyperlinkName = normalizedText
            .toUpperCase()
            .replace(/[^A-Z0-9]/g, "_")
            .replace(/_+/g, "_");

          Logger.debug(
            `wordFormatting.ts: Appendix logs: Inserting hyperlink for "${normalizedText}" to static bookmark "${targetHyperlinkName}"`,
            targetWord
          );

          linkRange.hyperlink = `#_${targetHyperlinkName}`;

          linkRange.font.name = originalFont.name;
          linkRange.font.size = originalFont.size;
          linkRange.font.italic = originalFont.italic;
        }

        await context.sync();
      }

      // --- 3. Clean up Extra Empty Lines ---
      // We reload paragraphs as the structure might have changed (e.g., lists created)
      processRange.load("paragraphs/items/text");

      await context.sync();

      let emptyCount = 0;

      // We iterate backwards when deleting items from a collection to avoid skipping items
      const updatedParagraphs = processRange.paragraphs.items;

      for (const para of updatedParagraphs) {
        para.inlinePictures.load("items");
      }

      await context.sync();

      for (let i = updatedParagraphs.length - 1; i >= 0; i--) {
        const para = updatedParagraphs[i];

        const text = para.text.trim();
        const hasInlinePictures = para.inlinePictures.items.length > 0;

        if (hasInlinePictures) {
          emptyCount = 0;
          continue;
        }

        if (text === "") {
          emptyCount++; // If we find more than one consecutive empty line, delete the current one

          if (emptyCount > 1) {
            para.delete();
          }
        } else {
          // Reset the counter when we find a non-empty paragraph
          emptyCount = 0;
        }
      }

      await context.sync();
    };

    if (existingContext) {
      await executeFormatting(existingContext);
    } else {
      await Word.run(async (context) => {
        await executeFormatting(context);
      });
    }
  } catch (error) {
    Logger.error("wordFormatting.ts: Error formatting document:", error, targetWord);

    if (error instanceof OfficeExtension.Error) {
      Logger.debug("wordFormatting.ts: Debug info:", error.debugInfo, targetWord);
    }
  }
};

/**
 * A report downloaded from app-0001 keeps plain "- " text in
 * its table cells because the assembler writes the hyphen list verbatim. A
 * section the reparse PRESERVES never runs through `formatSection`, so the
 * add-in's own bullet conversion has to be applied to those cells too.
 *
 * Deliberately narrow: only table-cell paragraphs are read, split and turned
 * into real Word bullets. Headers, body text, ordering and draft styling of
 * the rest of the section are left exactly as the document already has them,
 * so re-adopting a user's edited section can never reformat their work.
 */
export const formatTableCellLists = async (targetWord: string) => {
  try {
    await Word.run(async (context) => {
      const startBookmark = context.document.getBookmarkRangeOrNullObject(targetWord + "_START");
      const endBookmark = context.document.getBookmarkRangeOrNullObject(targetWord + "_END");

      startBookmark.load("isNullObject");
      endBookmark.load("isNullObject");
      await context.sync();

      if (startBookmark.isNullObject || endBookmark.isNullObject) {
        Logger.warn("wordFormatting.ts: Table-cell list pass skipped, bookmarks not found.", targetWord);
        return;
      }

      const paragraphs = startBookmark.expandTo(endBookmark).paragraphs;
      paragraphs.load("items/text");
      await context.sync();

      const parentCells = paragraphs.items.map((para) => {
        const parentCell = para.parentTableCellOrNullObject;
        parentCell.load("isNullObject");
        return parentCell;
      });
      await context.sync();
      const isTableCell = parentCells.map((cell) => !cell.isNullObject);

      // Split cells whose single paragraph still holds several newline-joined
      // "- " items into one paragraph each, matching `getTableCellListLines`.
      let splitAnyParagraph = false;
      for (let index = 0; index < paragraphs.items.length; index++) {
        if (!isTableCell[index]) continue;

        const listLines = getTableCellListLines(paragraphs.items[index].text);
        if (!listLines) continue;

        const paragraph = paragraphs.items[index];
        paragraph.insertText(listLines[0], Word.InsertLocation.replace);
        let previous = paragraph;
        for (const listLine of listLines.slice(1)) {
          previous = previous.insertParagraph(listLine, Word.InsertLocation.after);
        }
        splitAnyParagraph = true;
      }

      if (splitAnyParagraph) {
        await context.sync();
        paragraphs.load("items/text");
        await context.sync();
      }

      const splitParentCells = paragraphs.items.map((para) => {
        const parentCell = para.parentTableCellOrNullObject;
        parentCell.load("isNullObject");
        return parentCell;
      });
      await context.sync();

      let activeListId: number | null = null;
      for (let index = 0; index < paragraphs.items.length; index++) {
        if (splitParentCells[index].isNullObject) {
          activeListId = null;
          continue;
        }

        const paragraph = paragraphs.items[index];
        const bulletMatch = paragraph.text.match(/^(\s*)([-*+~])\s+/);
        if (!bulletMatch) {
          activeListId = null;
          continue;
        }

        const markerRange = paragraph.search(bulletMatch[0], { matchCase: true, matchWholeWord: false }).getFirst();
        markerRange.delete();

        let list: Word.List;
        if (activeListId !== null) {
          list = paragraph.attachToList(activeListId, 0);
        } else {
          list = paragraph.startNewList();
          list.load("id");
          await context.sync();
          activeListId = list.id;
        }

        list.setLevelBullet(0, Word.ListBullet.solid);
        paragraph.listItem.level = 0;
        await applyListParagraphFormat(paragraph);
      }

      await context.sync();

      try {
        const shouldGenerateTodoComments = shouldGenerateWordComment("JENLY_GENERATE_COMMENTS");
        const sectionRange = startBookmark.expandTo(endBookmark);
        await wordTask(context, "TODO:", sectionRange, true, shouldGenerateTodoComments);
        await wordTask(context, "information not available in the provided context", sectionRange, true);

        // Normalize any light-gray runs (#D9D9D9), yellow highlights, or inherited template font sizes in the preserved section
        const draftStyles = await loadFontStyle("draftStyles").catch(() => null);
        const draftColor = draftStyles?.fontColor || "#1F6B3E";
        const draftFont = draftStyles?.fontFamily || "Courier";
        const draftSize = draftStyles?.fontSize || 10;

        paragraphs.load("items/text, items/style, items/styleBuiltIn, items/font/name, items/font/size, items/font/color, items/font/bold, items/runs");
        await context.sync();
        for (const para of paragraphs.items) {
          (para as any).runs?.load?.("items/text, items/font/name, items/font/size, items/font/color, items/font/bold, items/font/highlightColor");
        }
        await context.sync();

        for (let pIdx = 0; pIdx < paragraphs.items.length; pIdx++) {
          const para = paragraphs.items[pIdx];
          if (isTemplateHeadingParagraph(para, pIdx, paragraphs.items.length)) {
            // NEVER reformat or recolor document template section headings!
            continue;
          }

          const paraText = para.text || "";
          const isHeadingPara =
            /^(Option [A-Z]:|MAH (conclusion|comment):|\d+(\.\d+)+\.?|\*\*)/i.test(paraText.trim()) ||
            para.font?.bold === true;

          if (para.font) {
            if (para.font.size && para.font.size !== draftSize) {
              para.font.size = draftSize;
            }
            if (para.font.name && (para.font.name === "Arial" || para.font.name === "Times New Roman" || para.font.name === "Calibri")) {
              para.font.name = draftFont;
            }
            if (isHeadingPara) {
              para.font.color = draftColor;
              para.font.bold = true;
            }
          }

          const runs = (para as any).runs;
          if (runs && runs.items) {
            for (const item of runs.items) {
              if (item.font) {
                if (item.font.highlightColor) {
                  const hl = String(item.font.highlightColor).toLowerCase();
                  if (hl === "yellow" || hl === "#ffff00") {
                    item.font.highlightColor = null;
                  }
                }
                if (item.font.color) {
                  const col = String(item.font.color).toUpperCase().replace("#", "");
                  if (["D9D9D9", "595959", "7F7F7F", "808080", "A6A6A6", "E7E6E6"].includes(col)) {
                    item.font.color = draftColor;
                  }
                }
                if (item.font.size && item.font.size !== draftSize) {
                  item.font.size = draftSize;
                }
                if (item.font.name && (item.font.name === "Arial" || item.font.name === "Times New Roman" || item.font.name === "Calibri")) {
                  item.font.name = draftFont;
                }
                if (isHeadingPara) {
                  item.font.color = draftColor;
                  item.font.bold = true;
                }
              }
            }
          }
        }
        await context.sync();
      } catch (todoError) {
        Logger.warn("wordFormatting.ts: Preserved section styling pass failed:", todoError, targetWord);
      }
    });
  } catch (error) {
    Logger.warn("wordFormatting.ts: Table-cell list pass failed:", error, targetWord);
  }
};
